import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { LEGAL_FAQ_CATEGORIES } from "@/content/legal-corpus/faq-defs";

const ADVICE_TH = [
  "ตรวจหนังสือรับรองนิติบุคคลและอำนาจลงนามของคู่สัญญาจากกรมพัฒนาธุรกิจการค้าก่อนลงนามทุกครั้ง เพราะผู้ลงนามที่ไม่มีอำนาจทำให้สัญญาไม่ผูกพันบริษัท",
  "ทำสัญญาสองภาษาและระบุให้ชัดว่าฉบับภาษาใดใช้บังคับหากถ้อยคำขัดกัน พร้อมกำหนดกฎหมายที่ใช้บังคับและวิธีระงับข้อพิพาทไว้ในฉบับเดียวกัน",
  "ประเมินอายุความก่อนเริ่มเจรจา คดีละเมิดมีเพียงหนึ่งปีนับแต่รู้เหตุและรู้ตัวผู้กระทำ ส่วนความผิดอันยอมความได้ต้องร้องทุกข์ภายในสามเดือน",
  "เก็บหลักฐานตั้งแต่วันแรกในรูปแบบที่ศาลรับฟังได้ ทั้งสัญญาต้นฉบับ ใบเสร็จ สลิปโอนเงิน อีเมล และข้อความสนทนาที่แสดงวันเวลาและคู่สนทนา",
  "จดทะเบียนการเช่าอสังหาริมทรัพย์ที่เกินสามปีต่อสำนักงานที่ดิน มิฉะนั้นตามมาตรา 538 จะบังคับได้เพียงสามปีแม้ระบุในสัญญาไว้ 30 ปี",
  "หากเลือกอนุญาโตตุลาการ ให้ระบุสถาบัน จำนวนอนุญาโตตุลาการ สถานที่ดำเนินกระบวน และภาษาที่ใช้ให้ครบ เพื่อไม่ให้เสียเวลาโต้แย้งเรื่องกระบวนการ",
  "แปลเอกสารสำคัญเป็นภาษาไทยไว้ล่วงหน้าเมื่อคาดว่าจะขึ้นศาลไทย เพราะศาลดำเนินกระบวนพิจารณาเป็นภาษาไทยและต้องยื่นคำแปลประกอบ",
  "ก่อนตั้งโครงสร้างผู้ถือหุ้น ตรวจว่าธุรกิจอยู่ในบัญชีท้าย พ.ร.บ.การประกอบธุรกิจของคนต่างด้าวหรือไม่ แล้วเลือกช่องทางที่เปิดเผยได้ เช่น ใบอนุญาต บีโอไอ หรือสนธิสัญญาไมตรี",
];

const CAUTIONS_TH = [
  "ใช้สัญญาแม่แบบจากต่างประเทศทั้งฉบับโดยไม่ปรับให้เข้ากับ ป.พ.พ. ทำให้บางข้อบังคับไม่ได้ในศาลไทย",
  "ตั้งเบี้ยปรับสูงมากโดยไม่มีที่มาของตัวเลข ซึ่งศาลลดลงได้ตามมาตรา 383 จึงไม่ได้ช่วยเพิ่มการคุ้มครองอย่างที่เข้าใจ",
  "เลิกจ้างด้วยวาจาหรือไม่ระบุเหตุไว้ในหนังสือเลิกจ้าง ทำให้ยกเหตุยกเว้นค่าชดเชยตามมาตรา 119 ขึ้นต่อสู้ภายหลังไม่ได้",
  "ให้คนไทยถือหุ้นแทนเพื่อเลี่ยงสัดส่วนต่างชาติ ซึ่งมาตรา 36 กำหนดโทษทั้งสองฝ่ายและศาลสั่งเลิกกิจการได้",
  "ทำสัญญาระหว่างผู้ถือหุ้นแล้วไม่แก้ข้อบังคับบริษัทให้สอดคล้อง ทำให้ข้อตกลงไม่ผูกพันบริษัทและบุคคลภายนอก",
  "รอเจรจากันเองในคดีฉ้อโกงจนเลยสามเดือน ทำให้คดีอาญาขาดอายุความร้องทุกข์",
  "โพสต์วิจารณ์คู่กรณีในสื่อออนไลน์ระหว่างมีข้อพิพาท ซึ่งเสี่ยงถูกฟ้องหมิ่นประมาทตามมาตรา 328 กลับมาอีกคดี",
  "ปล่อยให้คำพิพากษาถึงที่สุดเกินสิบปีโดยไม่ตั้งเรื่องบังคับคดี ทำให้สิ้นสิทธิบังคับคดีตามคำพิพากษานั้น",
];

export const Route = createFileRoute("/legal/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/legal-corpus");
    return { faqs: m.LEGAL_FAQ_TH };
  },
  component: LegalFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ที่ปรึกษากฎหมายสำหรับชาวต่างชาติ — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องร่างและตรวจสัญญาสองภาษา กฎหมายแรงงานและค่าชดเชยตามมาตรา 118 สัญญาเช่าและข้อพิพาทอสังหาริมทรัพย์ สัญญาระหว่างผู้ถือหุ้น อายุความและการบังคับคดี อนุญาโตตุลาการ และการรับรองเอกสารกฎหมาย",
        path: "/legal/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/legal/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ ที่ปรึกษากฎหมายสำหรับชาวต่างชาติ", url: `${site}/legal/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/legal/faq")) },
      ],
    };
  },
});

function LegalFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>ที่ปรึกษากฎหมายสำหรับชาวต่างชาติ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ที่ปรึกษากฎหมายสำหรับชาวต่างชาติในไทย สัญญา แรงงาน และข้อพิพาท
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          รวบรวมคำถาม {faqs.length} ข้อ พร้อมคำตอบที่อ้างอิงประมวลกฎหมายแพ่งและพาณิชย์ ประมวลกฎหมายวิธีพิจารณาความแพ่งและอาญา
          พ.ร.บ.คุ้มครองแรงงาน พ.ศ. 2541 พ.ร.บ.อนุญาโตตุลาการ พ.ศ. 2545 และ พ.ร.บ.การประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542
        </p>

        <section aria-label="หมวดคำถาม" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เลือกอ่านตามหมวด</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {LEGAL_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/legal/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleTh}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descTh}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_TH} locale="th" />
        <ServiceCautionBlock items={CAUTIONS_TH} locale="th" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้ทนายความประเมินเรื่องของท่านก่อนตัดสินใจ</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งลักษณะข้อพิพาท คู่กรณี วันที่เกิดเหตุ และเอกสารที่มีอยู่ ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <div className="mt-8 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/legal/faq" className="text-primary underline">Read this page in English →</a>
        </div>
        <FaqSourceNote topic="legal" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}